import {
  YES_NO,
  applicantSection,
  feeProofAttachment,
  otherAttachments,
  parcelsField,
  proxyAttachments,
  replySection,
  representationSection,
} from "modules/ebiForms/common";
import { EbiCondition, EEbiFormCategory, EEbiFormOffice, IEbiFormDefinition } from "modules/ebiForms/typings";

/**
 * G7. Wniosek o wydanie zaświadczenia, czy działka jest objęta uproszczonym planem urządzenia lasu (albo decyzją
 * z art. 19 ust. 3 ustawy o lasach) — art. 19 ust. 2–3 i art. 37a ustawy z 28.09.1991 r. o lasach, art. 217–218 KPA;
 * opłata: część II pkt 21 załącznika do ustawy o opłacie skarbowej (17 zł). Brak wzoru ogólnopolskiego; układ jak wzór
 * Starostwa Powiatowego w Świebodzinie (https://samorzad.gov.pl/attachment/8af65983-a927-411b-a839-6a924126c49c,
 * pobrany 22.10.2026): działki, obręb, gmina, cel, właściciel, sposób odbioru, dowód opłaty. Interes prawny osoby
 * niebędącej właścicielem — praktyka urzędów (dokument nieobowiązkowy). Liczba egzemplarzy — opłata od każdego.
 */
const NOT_OWNER: EbiCondition = { field: "wlascicielDzialek", equals: "nie" };

const G7: IEbiFormDefinition = {
  code: "G7",
  version: 1,
  title: "Wniosek o wydanie zaświadczenia o objęciu działki uproszczonym planem urządzenia lasu",
  category: EEbiFormCategory.Environment,
  office: EEbiFormOffice.County,
  description: "Zaświadczenie, czy działka jest objęta uproszczonym planem urządzenia lasu lub decyzją starosty — np. do aktu notarialnego.",
  keywords: ["las", "uproszczony plan urządzenia lasu", "upul", "zaświadczenie", "notariusz", "działka leśna", "pierwokup"],
  organ: "Starosta (prezydent miasta na prawach powiatu)",
  legalBasis: "art. 19 ust. 2–3 ustawy z dnia 28 września 1991 r. o lasach; art. 217 i art. 218 Kodeksu postępowania administracyjnego",
  request:
    "Wnoszę o wydanie zaświadczenia, czy działki wskazane we wniosku są objęte uproszczonym planem urządzenia lasu lub inwentaryzacją stanu lasów albo czy została dla nich wydana decyzja na podstawie art. 19 ust. 3 ustawy z dnia 28 września 1991 r. o lasach.",
  feeLabel: "17 zł",
  fee: "17 zł opłaty skarbowej za każdy egzemplarz zaświadczenia; pełnomocnictwo — 17 zł.",
  deadlineLabel: "do 7 dni",
  deadline: "Bez zbędnej zwłoki, nie później niż w 7 dni (art. 217 § 3 KPA).",
  sections: [
    applicantSection(),
    representationSection(),
    {
      code: "dzialkiSekcja",
      title: "Działki, których dotyczy zaświadczenie",
      fields: [
        { code: "gmina", type: "text", label: "Gmina, na terenie której położone są działki", required: true, width: "half", maxLength: 100 },
        { code: "ulica", type: "text", label: "Ulica (jeśli jest)", width: "half", maxLength: 200 },
        parcelsField(),
        {
          code: "wlascicielDzialek",
          type: "radio",
          label: "Czy jesteś właścicielem (współwłaścicielem) działek?",
          required: true,
          options: YES_NO,
        },
        { code: "wlasciciel", type: "text", label: "Właściciel działki (działek)", required: true, visibleWhen: NOT_OWNER, maxLength: 300 },
        {
          code: "interesPrawny",
          type: "textarea",
          label: "Interes prawny w uzyskaniu zaświadczenia",
          hint: "Nieobowiązkowo — np. zamiar zakupu działki na podstawie umowy przedwstępnej (art. 217 § 2 pkt 2 KPA).",
          visibleWhen: NOT_OWNER,
          maxLength: 1000,
        },
      ],
    },
    {
      code: "zaswiadczenie",
      title: "Zaświadczenie",
      fields: [
        {
          code: "cel",
          type: "text",
          label: "Cel wydania zaświadczenia",
          required: true,
          width: "twoThirds",
          maxLength: 300,
          placeholder: "np. do aktu notarialnego — sprzedaż działki",
        },
        { code: "egzemplarze", type: "number", label: "Liczba egzemplarzy", required: true, min: 1, max: 10, defaultValue: "1", width: "third" },
        {
          code: "kwotaOplaty",
          type: "fee",
          label: "Opłata skarbowa do wniesienia",
          feeAmount: 17,
          feeUnitField: "egzemplarze",
          hint: "17 zł za każdy egzemplarz zaświadczenia — na rachunek urzędu gminy właściwego dla siedziby starostwa.",
        },
      ],
    },
    replySection("Sposób odbioru zaświadczenia"),
  ],
  attachments: [
    feeProofAttachment("Dowód zapłaty opłaty skarbowej za zaświadczenie"),
    {
      code: "interesPrawnyDokument",
      label: "Dokument potwierdzający interes prawny (np. umowa przedwstępna)",
      hint: "Praktyka urzędów — nieobowiązkowo.",
      visibleWhen: NOT_OWNER,
    },
    ...proxyAttachments(),
    otherAttachments(),
  ],
};

export default G7;
